import { useMemo, useState } from 'react'
import { Plus, Receipt, Pencil, Trash2, CheckCircle2, Settings2 } from 'lucide-react'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { GastoFormDialog } from '@/components/gastos/GastoFormDialog'
import { GestionarCategoriasGastoDialog } from '@/components/gastos/GestionarCategoriasGastoDialog'
import type { GastoFormValues } from '@/components/gastos/forms/gastoSchema'
import {
  useGastos,
  useCategoriasGasto,
  useCrearGasto,
  useCrearTransferencia,
  useEditarGasto,
  useEliminarGasto,
} from '@/hooks/useGastos'
import { useCuentas } from '@/hooks/useMovimientos'
import { usePermissions } from '@/hooks/usePermissions'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { MovimientoConCliente, Database } from '@/types/database'

type MovimientoInsert = Database['public']['Tables']['movimientos']['Insert']

const buildInsert = (v: GastoFormValues): Omit<MovimientoInsert, 'organizacion_id'> => ({
  tipo: 'Gasto',
  cliente_id: null,
  cuenta_id: v.cuenta_id,
  categoria_id: v.categoria_id,
  concepto: v.concepto,
  fecha: v.fecha,
  // `debe` es la fuente de verdad del monto (decisión 12); `monto` se replica por compat.
  debe: v.monto,
  haber: 0,
  monto: v.monto,
  moneda: v.moneda,
  estado_pago: v.estado_pago,
  observaciones: v.observaciones ?? null,
})

export function Gastos() {
  const { puede } = usePermissions()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [gastoEditar, setGastoEditar] = useState<MovimientoConCliente | null>(null)
  const [gastoEliminar, setGastoEliminar] = useState<MovimientoConCliente | null>(null)
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [gestionarCategoriasOpen, setGestionarCategoriasOpen] = useState(false)

  const { data: gastos = [], isLoading } = useGastos({
    categoriaId: filtroCategoria !== 'todas' ? filtroCategoria : undefined,
    estadoPago:
      filtroEstado !== 'todos' ? (filtroEstado as 'pagado' | 'pendiente') : undefined,
  })
  const { data: categorias = [] } = useCategoriasGasto()
  const { data: cuentas = [] } = useCuentas()

  const crearGasto = useCrearGasto()
  const crearTransferencia = useCrearTransferencia()
  const editarGasto = useEditarGasto()
  const eliminarGasto = useEliminarGasto()

  const categoriasMap = useMemo(
    () => new Map(categorias.map((c) => [c.id, c.nombre])),
    [categorias],
  )
  const cuentasMap = useMemo(
    () => new Map(cuentas.map((c) => [c.id, c.nombre])),
    [cuentas],
  )

  const handleSubmit = async (values: GastoFormValues) => {
    if (gastoEditar) {
      await editarGasto.mutateAsync({ id: gastoEditar.id, data: buildInsert(values) })
    } else if (values.es_transferencia) {
      // El par egreso/ingreso lo crea la RPC atómica; concepto y moneda los define el server.
      await crearTransferencia.mutateAsync({
        cuentaOrigenId: values.cuenta_id,
        cuentaDestinoId: values.cuenta_destino_id!,
        monto: values.monto,
        fecha: values.fecha,
        observaciones: values.observaciones ?? null,
      })
    } else {
      await crearGasto.mutateAsync(buildInsert(values))
    }
  }

  const isSubmitting =
    crearGasto.isPending || crearTransferencia.isPending || editarGasto.isPending

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Gastos del estudio"
        description="Registro de gastos internos del estudio"
      />

      <div className="flex-1 flex flex-col p-6 gap-6 min-h-0">
        <div className="shrink-0 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="flex flex-wrap gap-3">
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las categorías</SelectItem>
                {categorias.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado de pago" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los estados</SelectItem>
                <SelectItem value="pagado">Pagado</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 shrink-0">
            {puede.gestionarCategoriasGasto && (
              <Button variant="outline" onClick={() => setGestionarCategoriasOpen(true)}>
                <Settings2 className="mr-2 h-4 w-4" />
                Categorías
              </Button>
            )}
            {puede.crearGasto && (
              <Button onClick={() => { setGastoEditar(null); setDialogOpen(true) }}>
                <Plus className="mr-2 h-4 w-4" />
                Nuevo gasto
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <p className="text-center text-muted-foreground py-8">Cargando gastos...</p>
        ) : gastos.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Receipt className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No hay gastos registrados</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="p-3 font-medium">Fecha</th>
                      <th className="p-3 font-medium">Concepto</th>
                      <th className="p-3 font-medium">Categoría</th>
                      <th className="p-3 font-medium">Cuenta</th>
                      <th className="p-3 font-medium text-right">Monto</th>
                      <th className="p-3 font-medium">Estado</th>
                      <th className="p-3 font-medium text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gastos.map((gasto) => (
                      <tr key={gasto.id} className="border-b last:border-0">
                        <td className="p-3 whitespace-nowrap">{formatDate(gasto.fecha)}</td>
                        <td className="p-3">{gasto.concepto}</td>
                        <td className="p-3">
                          <Badge variant={gasto.es_transferencia ? 'outline' : 'secondary'}>
                            {gasto.es_transferencia
                              ? 'Transferencia'
                              : (categoriasMap.get(gasto.categoria_id ?? '') ?? 'Sin categoría')}
                          </Badge>
                        </td>
                        <td className="p-3">{cuentasMap.get(gasto.cuenta_id ?? '') ?? '-'}</td>
                        <td className="p-3 text-right whitespace-nowrap font-medium">
                          {/* la pata ingreso de una transferencia lleva el monto en haber (debe=0) */}
                          {formatCurrency(gasto.debe || gasto.haber || gasto.monto || 0)}{' '}
                          <span className="text-xs text-muted-foreground">{gasto.moneda ?? 'ARS'}</span>
                        </td>
                        <td className="p-3">
                          <Badge variant={gasto.estado_pago === 'pagado' ? 'default' : 'outline'}>
                            {gasto.estado_pago === 'pagado' ? 'Pagado' : 'Pendiente'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            {puede.editarGasto &&
                              !gasto.es_transferencia &&
                              gasto.estado_pago === 'pendiente' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Marcar como pagado"
                                onClick={() =>
                                  editarGasto.mutate({
                                    id: gasto.id,
                                    data: { estado_pago: 'pagado' },
                                  })
                                }
                              >
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              </Button>
                            )}
                            {puede.editarGasto && !gasto.es_transferencia && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Editar"
                                onClick={() => { setGastoEditar(gasto); setDialogOpen(true) }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {puede.eliminarGasto && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setGastoEliminar(gasto)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <GastoFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) setGastoEditar(null)
        }}
        gasto={gastoEditar}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />

      <GestionarCategoriasGastoDialog
        open={gestionarCategoriasOpen}
        onOpenChange={setGestionarCategoriasOpen}
      />

      <AlertDialog
        open={!!gastoEliminar}
        onOpenChange={(open) => !open && setGastoEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {gastoEliminar?.es_transferencia ? '¿Eliminar transferencia?' : '¿Eliminar gasto?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {gastoEliminar?.es_transferencia ? (
                <>
                  Se eliminarán <strong>ambos movimientos</strong> del traslado (
                  <strong>{gastoEliminar?.concepto}</strong> y su contraparte). Esta acción
                  no se puede deshacer.
                </>
              ) : (
                <>
                  Se eliminará el gasto <strong>{gastoEliminar?.concepto}</strong>. Esta
                  acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (gastoEliminar) {
                  eliminarGasto.mutate(gastoEliminar.id, {
                    onSuccess: () => setGastoEliminar(null),
                  })
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminarGasto.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
